import type { SetStateAction } from 'react';
import { useMemo, useState } from 'react';

export const useStateWithLocalStorage = <TValue extends number | string | null | boolean | object>(
  defaultValue: TValue,
  localStorageKey: string
): [TValue, (valueSetter: SetStateAction<TValue>) => void] => {
  const initialValue = useMemo(() => {
    return getLocalStorageObject<TValue>(localStorageKey) ?? defaultValue;
  }, [defaultValue, localStorageKey]);
  const [value, setValueInState] = useState<TValue>(initialValue);
  const setValue = (newValueOrFn: SetStateAction<TValue>) => {
    setValueInState(oldValue => {
      const newValue = typeof newValueOrFn === 'function' ? newValueOrFn(oldValue) : newValueOrFn;
      setLocalStorageObject(localStorageKey, newValue);
      return newValue;
    });
  };

  return [value, setValue];
};

export const getLocalStorageObject = <TValue>(key: string): TValue | undefined => {
  try {
    const itemInStorage = localStorage.getItem(key);
    return itemInStorage ? (JSON.parse(itemInStorage) as TValue) : undefined;
  } catch {
    return undefined;
  }
};

export const setLocalStorageObject = <TValue>(key: string, value: TValue) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
};
